import { configureStore, createListenerMiddleware, type UnknownAction } from '@reduxjs/toolkit';
import { customerUserSlice } from '@src/components/DetailsDrawer/Customer/CustomerUserDetailsDrawer/state/slice';
import { preTradeSliceReducer } from '@src/components/PreTrade/redux/preTradeSlice';
import {
  portfolioViewLayoutSliceReducer,
  setupListeners as setupPortfolioListeners,
} from '@src/containers/Portfolio/PortfolioManagement/stateManagement/portfolioViewLayoutSlice';
import { filterSliceReducer } from '@src/containers/Trading/Markets/Options/Filter/filterSlice';
import {
  createAppConfigSlice,
  setupListeners as setupAppConfigListeners,
} from '@src/providers/AppConfigRedux/AppConfigSlice';
import { appNotificationsStreamingApi } from '@src/providers/AppNotifications/redux/AppNotificationsApi';
import {
  appNotificationsSliceReducer,
  setupListeners as setupAppNotificationsListeners,
} from '@src/providers/AppNotifications/redux/AppNotificationsSlice';
import { coinMetricsCredentialsReducer } from '@src/providers/CoinMetricsApiProvider/coinMetricsCredentialsSlice';
import { getWsClient, type IWebSocketClient } from '@talos/kyoko';
import { enableArrayMethods, enableMapSet } from 'immer';
import { ReplaySubject } from 'rxjs';
import { appLayoutSliceReducer } from '../../components/AppLayout/AppLayoutSlice';
import { multilegComboSliceReducer } from '../../components/MultilegCombo/MultilegComboSlice';
import { activeOrderCardsSliceReducer } from '../../components/OMS/Cards/ActiveOrderCardsSlice';
import {
  careOrderSliceReducer,
  setupListeners as setupCareOrderFormListeners,
} from '../../components/OMS/CareOrderFormView/CareOrderSlice';
import { detailsDrawerSliceReducer } from '../../components/OMS/Details/DetailsDrawerSlice';
import { manualDealSliceReducer } from '../../components/OMS/ManualDealTradeView/ManualDealSlice';
import { manualOrderFillSliceReducer } from '../../components/OMS/ManualOrderFillView/ManualOrderFillSlice';
import { setupKeyedManualRFQPricingListener } from '../../components/OMS/ManualRFQPricing/listeners';
import { manualRFQPricingReducer } from '../../components/OMS/ManualRFQPricing/ManualRFQPricingSlice';
import { manualTradeSliceReducer } from '../../components/OMS/ManualTradeView/ManualTradeSlice';
import {
  orderSliceReducer,
  setupListeners as setupOrderSliceListeners,
} from '../../components/OMS/NewOrder/OrderSlice';
import { rfqSliceReducer, setupListeners as setupRFQSliceListeners } from '../../components/OMS/NewRFQ/RFQSlice';
import { realTimeSettingsSliceReducer } from '../../components/OMS/OMSRealTimeSettingsSlice';
import { referenceDataSliceReducer } from '../../components/OMS/OMSReferenceDataSlice';
import { OMSSliceReducer } from '../../components/OMS/OMSSlice';
import { orderPresetSliceReducer } from '../../components/OMS/Presets/OrderPresetsSlice';
import { salesOrderSliceReducer, setupSalesOrderListeners } from '../../components/OMS/SalesOrder/SalesOrderSlice';
import { salesRFQSliceReducer, setupSalesRFQListeners } from '../../components/OMS/SalesRFQView/SalesRFQSlice';
import { baseRestApi } from '../../utils/rtkQuery/baseRestApi';
import { initialValues } from '../AppConfigProvider/initialValues';
import { navigationSliceReducer } from './navigationSlice';
import { getReduxDevToolsEnhancer } from './ReduxDevTools';
import { streamingDataSlice } from './streamingDataSlice';
import { timeReducers, timingMiddleware } from './timingMiddleware';
import { buildAppStateListener, type AppState } from './types';
import type { createUserInfoSlice } from './userInfoSlice';

// Enables optimized array method handling for Immer drafts.
enableArrayMethods();
// Lets us use Maps and Sets in Immer
enableMapSet();

/**  This subject will allow us to create a pattern similar to redux-observable (powerful middleware leveraging rxjs)
 * With this we are able to observe all actions going into this store. Our services can now create custom observables that react to this
 * See OrderService.observeForMarginCost as example.
 *
 * IMPORTANT: See deprecation note with {@link appStateActionsStream}
 *
 * HACK: Setting replay subject to buffer=2 to avoid missing actions when the reader is watching this stream late,
 * as needed by SalesOrderService.
 */
const appStateActionsSubject: ReplaySubject<UnknownAction> = new ReplaySubject(2);
/** Stream of App State actions
 *
 * @deprecated
 * USE WITH CARE: Make sure your reader is watching this stream early enough to not
 * miss any actions, since this is a hot observable.
 *
 * (Consider implementing a Redux Toolkit middleware so that the dispatch order doesn't matter, and the behavior
 * works directly against the state) */
export const appStateActionsStream = appStateActionsSubject.asObservable();

// It does nothing other than proxying the action into our Actions Subject
// Note: Subject receives action after the Store, again same as redux-observable
const customMiddleware = () => (next: (action: any) => void) => (action: any) => {
  next(action);
  appStateActionsSubject.next(action);
};

export const layoutListenerMiddleware = createListenerMiddleware<AppState>();

export const getAppStateStore = ({
  wsClient,
  userInfoSlice,
}: {
  wsClient?: IWebSocketClient<unknown>;
  userInfoSlice: ReturnType<typeof createUserInfoSlice>;
}) => {
  const appConfigSlice = createAppConfigSlice(
    initialValues(JSON.parse(userInfoSlice.getInitialState().user.AppConfig || '{}'))
  );

  const listenerMiddleware = createListenerMiddleware({
    extra: {
      wsClient: wsClient ?? getWsClient(),
    },
  });

  const startListening = buildAppStateListener(listenerMiddleware);

  setupPortfolioListeners(startListening);
  setupOrderSliceListeners(startListening);
  setupRFQSliceListeners(startListening);
  setupCareOrderFormListeners(startListening);
  setupAppConfigListeners(startListening);
  setupAppNotificationsListeners(startListening);
  setupSalesRFQListeners(startListening);
  setupSalesOrderListeners(startListening);
  setupKeyedManualRFQPricingListener(startListening);

  return configureStore({
    reducer: timeReducers({
      appConfig: appConfigSlice.reducer,
      coinMetricsCredentials: coinMetricsCredentialsReducer,
      appLayout: appLayoutSliceReducer,
      appNotifications: appNotificationsSliceReducer,
      appNotificationsStreamingApi: appNotificationsStreamingApi.reducer,
      baseRestApi: baseRestApi.reducer,
      cards: activeOrderCardsSliceReducer,
      careOrder: careOrderSliceReducer,
      customerUserDrawer: customerUserSlice,
      detailsDrawer: detailsDrawerSliceReducer,
      manualDeal: manualDealSliceReducer,
      manualOrderFill: manualOrderFillSliceReducer,
      manualRFQPricing: manualRFQPricingReducer,
      manualTrade: manualTradeSliceReducer,
      multilegCombo: multilegComboSliceReducer,
      navigation: navigationSliceReducer,
      OMS: OMSSliceReducer,
      optionsTab: filterSliceReducer,
      order: orderSliceReducer,
      portfolioViewLayout: portfolioViewLayoutSliceReducer,
      preTrade: preTradeSliceReducer,
      presets: orderPresetSliceReducer,
      realTimeSettings: realTimeSettingsSliceReducer,
      referenceData: referenceDataSliceReducer,
      rfq: rfqSliceReducer,
      salesOrder: salesOrderSliceReducer,
      salesRFQ: salesRFQSliceReducer,
      streamingData: streamingDataSlice.reducer,
      userInfo: userInfoSlice.reducer, // Temporary slice until we create with userApi when it's ready
    }),
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
        thunk: {
          extraArgument: {
            wsClient: wsClient ?? getWsClient(),
          },
        },
      }).concat(
        timingMiddleware,
        streamingDataSlice.middleware,
        listenerMiddleware.middleware,
        layoutListenerMiddleware.middleware,
        appNotificationsStreamingApi.middleware,
        baseRestApi.middleware,
        // customMiddleware needs to go last (unsure why at present)
        // - it gets in the way of registration of other middleware
        // - ideally it should be removed altogether in favor of 'actual' RTK middleware
        customMiddleware
      ),
    enhancers: getDefaultEnhancers => {
      const enhancers = getDefaultEnhancers();
      const reduxDevToolsExtension = getReduxDevToolsEnhancer();
      reduxDevToolsExtension && enhancers.push(reduxDevToolsExtension);
      return enhancers;
    },

    // https://github.com/reduxjs/redux-devtools/issues/643
    // The chrome devtools runs in a separate process as the app/browser tab, this means that all actions and state
    // needs to be serialized in order for the extension to be able to read it
    // OMS have references to big payloads and state object meaning that the extension will run very slow / unusable
    // but fast as if we run it as a native component (DevTools)
    devTools: false,
  });
};
